// @ts-check
import qs from "qs"
import {config} from "../../config"
import {throwRequestError, anySignal} from "./utils"

/**
 * @template T
 * @param {string} uri
 * @param {RequestConfig=} requestConfig
 * @returns {Promise<import('./types').FetchResponse<T>>}
 */
export default async function request(uri, requestConfig = {}) {
  const {
    body: requestBody,
    params: requestParams = {},
    timeout: requestTimeout = config.defaultRequestConfig.timeout,
    data: requestData,
    baseURL = config.defaultRequestConfig.baseURL,
    method: requestMethod = "GET",
    headers: requestHeaders,
    signal,
    ...fetchConfigs
  } = requestConfig

  // timeout fetch abort
  const abortController = new AbortController()
  let timeout = false

  const timeoutId = setTimeout(() => {
    timeout = true
    abortController.abort()
  }, requestTimeout)

  // compose fetch params and body
  const params = qs.stringify(requestParams, {
    encodeValuesOnly: true,
  })
  const body = requestBody || JSON.stringify(requestData)

  // compose fetch full URL
  const fetchURL = encodeURI(baseURL + uri) + (params ? `?${params}` : "")

  return fetch(fetchURL, {
    method: requestMethod,
    headers: {
      ...config.defaultRequestConfig.headers,
      ...requestHeaders,
    },
    ...fetchConfigs,
    body,
    signal: signal
      ? anySignal([abortController.signal, signal])
      : abortController.signal,
  })
    .then(async r => {
      const isJSONContent =
        r.headers.get("content-type").indexOf("application/json") !== -1

      const headers = {}
      if (r.headers) {
        for (const [key, value] of r.headers.entries()) {
          headers[key] = value
        }
      }

      /** @type {import('./types').FetchResponse<T>} */
      let fetchResponse = {}
      fetchResponse.status = r.status
      fetchResponse.data = isJSONContent ? await r.json() : await r.text()
      fetchResponse.headers = headers
      fetchResponse.statusText = r.status.toString()

      if (r.status >= 200 && r.status < 300) {
        return fetchResponse
      }
      throwRequestError(fetchResponse, {
        uri,
        ...requestConfig,
      })
    })
    .catch(err => {
      if ((err.name === "AbortError" || err.message === "Aborted") && timeout) {
        const timeoutErr = new Error(`Request Timeout of ${requestTimeout}ms.`)
        timeoutErr.name = "RequestTimeout"
        throw timeoutErr
      }
      throw err
    })
    .finally(() => {
      // clear timeout when request is final, success or failed
      clearTimeout(timeoutId)
    })
}
export {request as fetch}

/**
 * @typedef {import('./types').RequestConfig} RequestConfig
 */
